import { useUserMSPCheckHook } from '@nile/components';
import { cloneDeep } from 'lodash';
import { useState } from 'react';

interface User {
  name: string;
  userId: string;
  avatarFileName: string;
}

type eventType = 'UPDATE' | 'EXIT_PAGE' | 'REGISTER_PAGE' | 'UNREGISTER_PAGE';

interface PayloadType {
  event: eventType;
  page: {
    type: 'DETAIL';
    entity: 'Invoice';
    recordId: string;
  };
  epochMillis: string;
  type: 'SERVER_EVENT' | 'CLIENT_EVENT';
}

interface RequestPayloadType extends PayloadType {
  data: {
    allowSimultaneousPage: boolean;
  };
}

interface PushedToIntegratedAppErrorType {
  errorCode: number;
  message: string;
  params: {
    items: {
      itemId: number;
      name: string;
    }[];
  };
}

interface ResponsePayloadType extends PayloadType {
  user: User;
  data: {
    invoiceId: string;
    allowSimultaneousPage?: boolean;
    operation?: string;
    value: {
      pushedToIntegratedAppStatus: string;
      pushedToIntegratedAppError?: string;
      integratedAppInvoiceId?: string;
    };
  };
}

interface requestEventDataType {
  context: string;
  // windowsId: string;
  payload: RequestPayloadType;
}

interface responseEventDataType {
  context: string;
  payload: ResponsePayloadType;
}

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

export default function useGetInvoiceUpdates(
  invoiceId: string
): {
  invoiceData: {
    pushedToIntegratedAppStatus: string;
    pushedToIntegratedAppError?: PushedToIntegratedAppErrorType[];
    integratedAppInvoiceId?: string;
  };
  sendClientEvent: (event: eventType, invoiceID: string) => void;
} {
  const isMSP = useUserMSPCheckHook();
  const [invoiceData, setInvoiceData] = useState<{
    pushedToIntegratedAppStatus: string;
    pushedToIntegratedAppError?: PushedToIntegratedAppErrorType[];
    integratedAppInvoiceId?: string;
  }>({
    pushedToIntegratedAppStatus: '',
    pushedToIntegratedAppError: [],
    integratedAppInvoiceId: ''
  });

  function sendEvent(event: eventType, invoiceID: string) {
    const invoiceIdRegistered = sessionStorage.getItem(invoiceID);
    if (event === 'REGISTER_PAGE') {
      if (invoiceIdRegistered === 'REGISTER_PAGE') {
        return;
      }
      sessionStorage.setItem(invoiceID, 'REGISTER_PAGE');
    }

    if (
      (event === 'EXIT_PAGE' || event === 'UNREGISTER_PAGE') &&
      invoiceIdRegistered
    ) {
      sessionStorage.removeItem(invoiceID);
    }

    const postMessageData: requestEventDataType = {
      context: 'pageUpdate',
      payload: {
        type: 'CLIENT_EVENT',
        page: {
          type: 'DETAIL',
          entity: 'Invoice',
          recordId: invoiceID
        },
        data: {
          allowSimultaneousPage: true
        },
        event: event || 'REGISTER_PAGE',
        epochMillis: new Date().getTime().toString()
      }
    };
    if (isMSP) {
      broadcastChannel.postMessage(
        JSON.stringify({
          name: 'wsdata',
          data: postMessageData
        })
      );
    }
  }

  broadcastChannel.onmessage = (event) => {
    if (isMSP && event.data) {
      const eventData: responseEventDataType = JSON.parse(event.data);
      const { context, payload } = eventData;
      if (
        context === 'pageUpdate' &&
        payload?.page?.recordId === invoiceId &&
        payload?.type === 'SERVER_EVENT'
      ) {
        const clonedInvoiceData = cloneDeep(invoiceData);
        switch (payload.event) {
          case 'UPDATE':
            switch (payload.data?.operation) {
              case 'SYNC_FAILURE':
                setInvoiceData({
                  ...invoiceData,
                  pushedToIntegratedAppStatus: 'FAILURE',
                  pushedToIntegratedAppError: JSON.parse(
                    payload.data?.value?.pushedToIntegratedAppError
                  )
                });
                break;
              case 'SYNC_SUCCESS':
                setInvoiceData({
                  ...invoiceData,
                  pushedToIntegratedAppStatus: 'SUCCESS',
                  integratedAppInvoiceId:
                    payload.data?.value?.integratedAppInvoiceId
                });
                break;
              default:
                break;
            }
            break;
          case 'REGISTER_PAGE':
          case 'EXIT_PAGE':
          case 'UNREGISTER_PAGE':
          default:
            break;
        }
      }
    }
  };

  return {
    invoiceData: invoiceData,
    sendClientEvent: sendEvent
  };
}
